/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {BookingFilter} from "../types/app";
import {ArticleType} from "types/enums/ArticleType";
import {Article} from "types/entities/Article";
import {
    CURRENCY,
    CURRENCY_FRACTION_DIGITS,
    DEFAULT_LANGUAGE,
    GUEST_ADDRESS_MANDATORY,
    GUEST_PHONE_EMAIL_MANDATORY,
    IS_PROD,
    LOCALE,
    PAYMENT_TEST,
    REQUIRE_FULL_ADDRESS_IN_REGISTRATION,
    TIMER_COUNT,
    Translations,
} from "helpers/config";
import {ArticlesReducerState} from "store/articles/ArticlesReducer";
import store from "helpers/store";
import {PackageItemItemType} from "types/enums/PackageItemItemType";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {Package} from "types/entities/Package";
import {PersonType} from "types/enums/PersonType";
import {PackageItem} from "types/entities/PackageItem";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {PersonsType} from "types/enums/PersonsType";
import {PackagesCart} from "types/entities/PackagesCart";
import {Profile} from "types/entities/Profile";
import {PackageCompetition, PackageUpsell} from "store/booking/BookingAction";
import {BookingNavigationStep, BookingReducerState} from "store/booking/BookingReducer";
import {uniquePackageWithActivityItem} from "utils/packageUtils";
import {toast} from "react-toastify";
import {PriceOffer} from "types/entities/PriceOffer";
import {Driver} from "types/entities/Driver";
import i18n from "i18next";
import ajax from "../helpers/ajax";
import {ReservationDriver} from "types/entities/ReservationDriver";
import {UserType} from "types/enums/UserType";
import {parsePhoneNumberFromString} from "libphonenumber-js";
import {CalendarView} from "types/enums/CalendarView";
import {useTranslation} from "react-i18next";
// import {countries} from "country-flag-icons";

export const driverPlaceTextSuffix = (i: number) => {
    switch (i) {
        case 1:
            return i18n.t("st");
        case 2:
            return i18n.t("nd");
        case 3:
            return i18n.t("rd");
        default:
            return i18n.t("th");
    }
};

export const driverName = (driver: Driver) => {
    if (typeof driver.last_name === "undefined") {
        return driver.username || driver.name;
    }
    return driver.username || driver.name + " " + driver.last_name;
};

export const driverNameInvited = (driver: Driver) => {
    if (typeof driver.last_name === "undefined") {
        return driver.username || driver.name;
    }
    const lastNameWithFirstDigit = (driver.last_name || "").slice(0, 1) + ".";
    return driver.username || driver.name + " " + lastNameWithFirstDigit;
};

export const getTodayDate = (): number => {
    return Math.floor(Date.now() / 1000);
};

export const lapTime = (laptime: number): string => {
    return (laptime / 100).toFixed(2) + "s";
};

export const percentageTaxFormat = value => {
    return new Intl.NumberFormat(LOCALE, {
        style: "percent",
        currency: CURRENCY,
        minimumSignificantDigits: 2,
    }).format(value / 100);
};

export const currencyNumberFormat = value => {
    return new Intl.NumberFormat(LOCALE, {
        style: "currency",
        currency: CURRENCY,
        minimumFractionDigits: CURRENCY_FRACTION_DIGITS,
    }).format(value / 100);
};

export const currencyNumberFormatNoDigit = value => {
    return new Intl.NumberFormat(LOCALE, {
        style: "currency",
        currency: CURRENCY,
        minimumFractionDigits: 0,
    }).format(value / 100);
};

export const floatNumberFormat = (value, digits = CURRENCY_FRACTION_DIGITS) => {
    return new Intl.NumberFormat(LOCALE, {
        style: "decimal",
        minimumFractionDigits: digits,
    }).format(value / 100);
};

export const floatNumberFormatNoDigit = value => {
    return new Intl.NumberFormat(LOCALE, {
        style: "decimal",
        minimumFractionDigits: 0,
    }).format(value / 100);
};

export const getArticle = (articlesState: ArticlesReducerState, articleType: ArticleType, personType: PersonType, count: number) => {
    if (PAYMENT_TEST) {
        return articlesState.all?.find((article: Article) => article.article_id === 3000);
    }

    if (articleType === ArticleType.Action) {
        return articlesState[articleType][personType].find((article, index) => index === 0);
    } else {
        return articlesState[articleType][personType].find(article => article.race_count_total === count);
    }
};

export const getArticlePrices = (articlesState: ArticlesReducerState, articleType: ArticleType, personType: PersonType, count: number): PriceOffer => {
    const baseArticle: Article | undefined = getArticle(articlesState, ArticleType.Online, personType, 1);
    const article = getArticle(articlesState, articleType, personType, count);

    if (!article || !baseArticle) {
        return {
            price: 0,
            pricePerRace: 0,
            savings: 0,
        };
    }

    if (articleType === ArticleType.Action) {
        return {
            price: article?.article_value,
            pricePerRace: article?.article_value / article?.race_count_total,
            savings: baseArticle?.article_value * article?.race_count_total - article?.article_value,
        };
    } else {
        return {
            price: article.article_value,
            pricePerRace: article.article_value / article.race_count_total,
            savings: baseArticle.article_value * count - article.article_value,
        };
    }
};

export const getStatsNumberTextByType = (type: string | undefined, number: number) => {
    switch (type) {
        case "position":
            return number + " " + driverPlaceTextSuffix(number);
        case "time":
            return number + "s";
        case "number":
            return number;
        default:
            return number;
    }
};

export const getArticleById = (id: number): Article => {
    return <Article>store.getState().articlesState.all.find((article: Article) => article.id === id);
};

export function getEnumKeyByEnumValue<
    T extends {
        [index: string]: string;
    },
>(myEnum: T, enumValue: string): keyof T | null {
    const keys = Object.keys(myEnum).filter(x => myEnum[x] == enumValue);
    return keys.length > 0 ? keys[0] : null;
}

export const serializeFormData = (data: any, formData?: FormData, previousKey?: any) => {
    if (!formData) {
        formData = new FormData();
    }

    Object.keys(data).map(key => {
        const value = data[key];

        // example: object in an array (flags: Flag[])
        if (!(value instanceof File) && !(value instanceof FileList) && value instanceof Object && !Array.isArray(value)) {
            return serializeFormData(value, formData, key);
        }

        console.log("is file...", value);

        if (previousKey) {
            key = `${previousKey}[${key}]`;
        }

        if (value instanceof FileList) {
            console.log("is file... - FileList", value);
            const files = [...data[key]];
            files.map((file, index) => {
                // @ts-ignore We for sure know that formData IS set.
                formData.append(`${key}[${index}]`, file);
            });
        } else if (Array.isArray(value)) {
            console.log("is file... - Array", value);
            value.map((val, index) => {
                serializeFormData(val, formData, `${key}[${index}]`);
            });
        } else {
            console.log("is file... - ELSE", value);
            // @ts-ignore We for sure know that formData IS set.
            formData.append(key, value);
        }
    });
    return formData;
};

export const filterPackageItemsByType = (Package: Package, person_type: PackageItemPersonType | PersonType): PackageItem[] => {
    return Package.packageItems
        ?.filter(packageItem => packageItem.person_type === person_type)
        .filter(packageItem => packageItem.type === PackageItemItemType.PackageItem);
    // .sort((a, b) => {
    //     if(a.position === null && b.position === null) return 0;
    //     if(a.position === null ) return b.position as number;
    //     if(b.position === null ) return a.position as number;
    //     return (
    //         a.position < b.position ? -1 : 1
    //     );
    // });
};

export const personTypeLabel = (person_type: PackageItemPersonType | null) => {
    switch (person_type) {
        case PackageItemPersonType.Adult:
            return i18n.t("Adult person");
        case PackageItemPersonType.Child:
            return i18n.t("Child person");
        case PackageItemPersonType.Event:
            return i18n.t("Event person");
        case PackageItemPersonType.Family:
            return i18n.t("Family person");
        default:
            return i18n.t("Any person");
    }
};

export const translate = (translation: Translations, defaultLanguage: string = "") => {
    const language = store.getState().hallState.hall?.language.code;
    const userLanguage = store.getState().sessionState.language?.code;
    if (!defaultLanguage) {
        defaultLanguage = DEFAULT_LANGUAGE;
    }

    // @ts-ignore
    return translation[userLanguage] || translation[language] || translation[defaultLanguage] || translation["en"] || translation || "not found";
};

export const truncateTextToLength = (str: string, length?: number) => {
    if (!str) {
        return "";
    }
    if (!length) {
        length = 300;
    }

    const dots = str.length > length ? "..." : "";
    if (!str.length) return "";
    return str.substring(0, length) + dots;
};

const DurationReducer = (accumulator, cur: PackageItem) => {
    let accDuration = 0;
    const article = cur.article;
    if (article && article.race_count_total && typeof article !== undefined) {
        accDuration = article.race_count_total > 1 ? cur.duration * article.race_count_total : cur.duration;
    }

    if (isNaN(accumulator)) {
        return accDuration;
    }
    return accumulator + accDuration;
};

export const packageDuration = (packageRow: Package) => {
    const durationByPackageItems =
        uniquePackageWithActivityItem(packageRow, true).reduce(DurationReducer, 0) +
        uniquePackageWithActivityItem(packageRow, false).reduce(DurationReducer, 0);
    const durationByPackage = packageRow.duration;
    return durationByPackageItems > durationByPackage ? durationByPackageItems : durationByPackage;
};

export const getBreakClaimText = (index: number, type: string) => {
    const gameBreakClaims = ["View and compare the results"];

    const activityBreakClaims = [
        "Time to take a quick brake and move on to next activity",
        "You need to walk to another activity and get ready",
        "You're getting ready for your next activity",
        "You need to walk to another activity and get ready",
        "Time to take a quick brake and move on to next activity",
    ];

    return type === "activity" ? i18n.t(activityBreakClaims[index]).toString() : i18n.t(gameBreakClaims[index]).toString();
};

export const getPackageItemFromPackageCartItem = (packageCartItem: PackagesCartItem, package_: Package) => {
    if (package_.packageItems) {
        return package_.packageItems.find(packageItem => packageItem.id === packageCartItem.package_item_id);
    }
};

export const padMinutes = (minutes: number) => {
    return ("00" + minutes).slice(-2);
};

export const consoleDebugger = () => {
    debugger; // eslint-disable-line no-debugger
};

export const consoleLog = (...params) => {
    if (IS_PROD) {
        return;
    }
};

export const uniqueFilter = array => array.filter((v, i, a) => a.indexOf(v) === i);

export function redirectToReservationInquired(history, packagesCart: PackagesCart) {
    // /booking/inquiry/:cart_id/:cart_token
    history.replace("/booking/inquiry/" + packagesCart.id + "/" + packagesCart.token);
}

export const isAdult = (personsType: PersonType | PersonsType | string) => {
    return personsType === PersonType.Adult || personsType === PersonsType.Adult || personsType === "adult";
};

export const isChild = (personsType: PersonType | PersonsType | string) => {
    return personsType === PersonType.Child || personsType === PersonsType.Child || personsType === "child";
};

export const isFamily = (personsType: PersonsType | string) => {
    return personsType === PersonsType.Family || personsType === "family";
};

export const isUnknown = (personsType: PersonsType | string) => {
    return personsType === PersonsType.Unknown || personsType === "unknown";
};

export const validateByPersonType = (personsType: PersonsType | string): boolean => {
    switch (personsType) {
        case PersonsType.Adult:
            return isAdult(personsType);
        case PersonsType.Child:
            return isChild(personsType);
        case PersonsType.Family:
            return isFamily(personsType);

        default:
            return isUnknown(personsType);
    }
};

export const filterPersonsType = (filter: BookingFilter) => {
    if (filter.children > 0 && filter.adults > 0) {
        return PersonsType.Family;
    }
    if (filter.adults > 0) {
        return PersonsType.Adult;
    }
    if (filter.children > 0) {
        return PersonsType.Child;
    }
    return PersonsType.Unknown;
};

export const packagesCartTimeout = (packagesCart: PackagesCart) => {
    const timeNow = new Date().getTime();
    const packagesCartTime = new Date(packagesCart.updated_at).getTime();

    return packagesCartTime + TIMER_COUNT <= timeNow;
};

export const getCurrentDriver = (profile: Profile) => {
    return {
        identifier: +profile.id,
        name: profile.name,
        profile_picture_url: profile?.profile_picture_url || null,
        experience: profile.experience,
        person_type: profile.person_type,
        selected: false,
        races_count: profile.ticket_races_left,
        races_assigned_count: profile.ticket_races_left,
    };
};

export const getPersonTypeColorClass = (personType: PersonType) => {
    switch (personType) {
        case PersonType.Adult:
            return "text-blue-800 bg-blue-400";
        case PersonType.Child:
            return "text-green-800 bg-green-400";
        default:
            return "text-indigo-800 bg-indigo-400";
    }
};

export const getReservationTicketColorClass = (personType: PersonType, type: ArticleType) => {
    switch (personType) {
        case PersonType.Adult:
            if (type == ArticleType.Action) {
                return "text-indigo-800 bg-indigo-400";
            }
            return "text-blue-800 bg-blue-400";

        case PersonType.Child:
            if (type == ArticleType.Action) {
                return "text-blue-800 bg-blue-400";
            }
            return "text-green-800 bg-green-400";
        default:
            return "text-indigo-800 bg-indigo-400";
    }
};

export const appendQueryParams = filter => {
    return new URLSearchParams(filter as any).toString();
};

export const getPackageItemsWithoutGroupOptions = (Package: Package): PackageItem[] => {
    return Package.packageItems.filter(packageItem => packageItem.type === PackageItemItemType.Upsell).filter(packageItem => packageItem.extra?.group === null);
};

export const getPackageItemsWithGroupOptions = (Package: Package): PackageItem[] => {
    return Package.packageItems.filter(packageItem => packageItem.type === PackageItemItemType.Upsell).filter(packageItem => packageItem.extra.group !== null);
};

export const getSelectedUpsell = (upsells: PackageUpsell[], packageItem) => {
    return upsells.find(upsell => upsell.packageItem.id === packageItem.id);
};

export const getSelectedCompetition = (competitions: PackageCompetition[], packageItem) => {
    return competitions.find(competition => competition.packageItem.id === packageItem.id);
};

export const getNewStep = (bookingState: BookingReducerState, stepId: number): BookingNavigationStep => {
    const steps = bookingState.timeslotsAllowed ? bookingState.navigation.timeslotsAllowedSteps : bookingState.navigation.timeslotsNotAllowedSteps;
    return steps.find(step => step.id === stepId) || bookingState.navigation.currentStep;
};

export const getNewStepGift = (bookingState: BookingReducerState, stepId: number): BookingNavigationStep => {
    const steps = bookingState.timeslotsAllowed ? bookingState.navigation.timeslotsGiftAllowedSteps : bookingState.navigation.timeslotsNotAllowedSteps;
    return steps.find(step => step.id === stepId) || bookingState.navigation.currentStep;
};
export const sortPackageItemsByPosition = (packageItems: PackageItem[]) => {
    return packageItems.sort((a, b) => {
        if (a.position === null || b.position === null) return 1;
        return a.position > b.position ? 1 : -1;
    });
};

export const sortPackagesCartItemsByPosition = (packagesCartItems: PackagesCartItem[]) => {
    return packagesCartItems.sort((a, b) => {
        if (a.packageItem === null || b.packageItem === null) return 1;
        if (a.packageItem.position === null || b.packageItem.position === null) return 1;
        return a.packageItem.position > b.packageItem.position ? 1 : -1;
    });
};

export const packageItemUpsellPersonType = (bookingFilter, personType: PackageItemPersonType | null) => {
    if (!personType) return true;
    if (personType === PackageItemPersonType.Any) return true;
    if (bookingFilter.adults > 0 && personType === PackageItemPersonType.Adult) return true;
    if (bookingFilter.children > 0 && personType === PackageItemPersonType.Child) return true;
};

export const copyToClipboard = async (text?: string): Promise<boolean> => {
    try {
        const toCopy = text || location.href;
        await navigator.clipboard.writeText(toCopy);
        toast.success(i18n.t("Copied to clipboard") + ": " + toCopy);
        return true;
    } catch (err) {
        console.error("Failed to copy: ", err);
        return false;
    }
};

export const truncateString = (text: string, length: number, symbol?: string) => {
    if (typeof symbol === "undefined") {
        symbol = ".";
    }
    if (text.length > length) {
        return text.slice(0, length - 1) + "" + symbol;
    }
    return text;
};

export const subtractDaysFromDates = (dateFrom: Date, days: number): Date => {
    return new Date(dateFrom.setDate(dateFrom.getDate() - days));
};

export const range = (size, startAt = 0) => {
    // @ts-ignore
    return [...Array(size).keys()].map(i => i + startAt);
};

export const phoneValidation = (phone: any) => {
    if (phone.length === 0 || phone === 0 || phone === "") {
        return false;
    }
    const phoneTrimmed = phone.replace(/\s/g, "");
    const regex = /(\+\d{1,3}\s?)?((\(\d{3}\)\s?)|(\d{3})(\s|-?))(\d{3}(\s|-?))(\d{3,6})(\s?(([E|e]xt[:|.|]?)|x|X)(\s?\d+))?/g;
    return regex.test(phoneTrimmed);
};

export const emailValidation = (email: string) => {
    const emailTrimmed = email.replace(/\s/g, "");
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
    return regex.test(emailTrimmed);
};

export const checkAddress = (addressData: {city: string; street_address: string; postcode: string}) => {
    const url = "https://service.post.ch/zopa/app/api/addresschecker/v1/houses/search?noCache=";
    const timestamp = new Date().getTime();

    const numb = addressData?.street_address?.match(/\d/g);
    const houseNumber = numb ? numb.join("") : null;
    const {city, street_address, postcode} = addressData;
    const params = {
        city: city,
        street: street_address,
        limit: 60,
        houseNbr: houseNumber,
        noCache: timestamp,
    };
    const result = ajax.get(url, {params: params});
    console.log(result);
};

export const listOfAllRequiredFieldsForAccount = (account: Driver): string[] => {
    const baseFields = ["name", "last_name", "nickname", "birthdate"];
    const addressFields = ["address", "country", "zip", "town"];
    const contactFields = ["email", "phone"];
    const userTypeToRequiredFields: Record<UserType, string[]> = {
        [UserType.User]: [...baseFields, ...(REQUIRE_FULL_ADDRESS_IN_REGISTRATION ? addressFields : []), ...contactFields],
        [UserType.Guest]: [...baseFields, ...(GUEST_ADDRESS_MANDATORY ? addressFields : []), ...(GUEST_PHONE_EMAIL_MANDATORY ? contactFields : [])],
    };
    return userTypeToRequiredFields[UserType.Guest] || baseFields;
};

export const listOfAllMissingRequiredData = (account: Driver): string[] => {
    return listOfAllRequiredFieldsForAccount(account).filter(name => !account[name] || account[name] === "");
};

export const isValidPhoneNumberFormat = (phoneNumber: string) => {
    if (phoneNumber.startsWith("+")) {
        const parsePhone = parsePhoneNumberFromString(phoneNumber);
        return parsePhone?.isValid();
    }
    return false;
};

export const checkIfAllAccountHasCorrectData = (accounts: ReservationDriver[]): boolean => {
    const allAccountsHaveAllRequiredData = accounts.every(account => account?.driver && listOfAllMissingRequiredData(account.driver).length === 0);
    const allAccountsHaveCorrectPhoneData = accounts.every(account => account?.driver?.phone && isValidPhoneNumberFormat(account.driver.phone));
    return allAccountsHaveAllRequiredData && allAccountsHaveCorrectPhoneData;
};

export const getNewDateObject = (date: Date, preset: CalendarView, offset: number) => {
    if (preset === CalendarView.Day) {
        date.setDate(date.getDate() + offset);
    } else if (preset === CalendarView.Agenda) {
        date.setDate(date.getDate() + offset);
    } else if (preset === CalendarView.Week) {
        date.setDate(date.getDate() + offset * 7);
    } else if (preset === CalendarView.Month) {
        date.setMonth(date.getMonth() + offset);
    } else if (preset === CalendarView.Year) {
        date.setFullYear(date.getFullYear() + offset);
    }

    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1, // index from 0
        day: date.getDate(),
    };
};

// export const getCountryCodeFromCountryName = async (countryName: string): Promise<string | null> => {
//     const supportedLanguages = ['ar', 'cs', 'da', 'de', 'en', 'es', 'et', 'fi', 'fr', 'hr', 'hu', 'it', 'lt', 'lv', 'mn', 'nb', 'nl', 'nn', 'pl', 'pt', 'ro', 'ru', 'sk', 'sv', 'tr', 'uk', 'zh'];
//
//     const loadLanguagesPack = async () => {
//         for (const lang of supportedLanguages) {
//             try {
//                 const languagePack = await import(`i18n-iso-countries/langs/${lang}.json`);
//                 countries.registerLocale(languagePack);
//             } catch (ex) {
//                 console.log(`Could not load language: ${lang}`);
//             }
//         }
//     }
//
//     const getCountryCode = (countryName: string): string | null => {
//         loadLanguagesPack();
//         for (const lang of supportedLanguages) {
//             const code = countries.getAlpha2Code(countryName, lang);
//             if (code) {
//                 return code;
//             }
//         }
//         return null;
//     }
//
//     return await getCountryCode(countryName);
// }

export const REGEX_EMAIL = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;

export const useGetHelpTextForGroupName = () => {
    const {t} = useTranslation();

    const getGroupName = (groupName: string) => {
        switch (groupName) {
            case "Apero und Snacks":
                return [t("Tolle Snack-Angebote"), t("Vielfältige Apéro-Varianten")];
            case "Competitions":
                return [t("Echter Wettkampf statt nur Spiel"), t("Team-Adrenalin & Gänsehaut-Momente")];
            case "Warmes Essen":
                return [t("Drei fein komponierte Menüs mit Salat und Dessert"), t("Hausgemachte Klassiker: Pasta, Pizza oder Zürcher Geschnetzeltes")];
            case "Party Pakete":
                return [t("Perfekte Pakete für deinen Polterabend"), t("Tolle Kindergeburtstag Pakete")];
            case "Canapé Selection":
                return [t("Feine Canapés für besondere Momente"), t("Kleine Häppchen, grosser Genuss")];
            default:
                return [t("Perfekte Pakete für deinen Polterabend"), t("Tolle Kindergeburtstag Pakete")];
        }
    };
    return {
        getGroupName,
    };
};

export function toMapKey(input: string): string {
    return input
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
}
